import { useEffect, useRef, useState } from 'react';
import { Form, Offline, Screen, Section, StaffBar, TextField, afterSignIn, navigate, signIn, strings } from './kit/index.ts';

/**
 * `/sign-in`: staff email and password (a stopgap until staff-and-permissions,
 * cloud/src/auth/staff-accounts.ts). On success the cloud sets the session
 * cookie and the page goes back to the page that sent the browser here
 * (`?next=`), or else to the staff home.
 */
export function SignInPage({ staffHome }: { readonly staffHome: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const emailInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailInput.current?.focus();
  }, []);

  const submit = async () => {
    if (email.trim() === '' || password === '') {
      setProblem(strings.staff.signInMissing);
      return;
    }
    setProblem(undefined);
    setBusy(true);
    try {
      const answer = await signIn(email, password);
      if (answer.ok) {
        navigate(afterSignIn() ?? staffHome, { replace: true });
        return;
      }
      setPassword('');
      setProblem(answer.reason === 'wrong' ? strings.staff.signInFailed : strings.common.rateLimited(Math.ceil(answer.retryAfterSeconds / 60)));
    } catch (error) {
      if (!(error instanceof Offline)) throw error;
      setProblem(strings.common.offline);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen kind="office" title={strings.staff.signInTitle} bar={<StaffBar />}>
      <Section>
        <Form onSubmit={() => void submit()} submit={strings.staff.signIn} busy={busy} busyLabel={strings.staff.signingIn} failure={problem}>
          <TextField
            ref={emailInput}
            label={strings.staff.emailLabel}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <TextField
            label={strings.staff.passwordLabel}
            type="password"
            autoComplete="current-password"
            name="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </Form>
      </Section>
    </Screen>
  );
}
